import React, { RefObject, useCallback, useEffect, useRef } from "react";
import MessageList from "./MessageList";
import {
	type LoanTapePreviewColumn,
	type SelectedVersions,
	UIMessage,
} from "./Chat/Chat.types";
import type { CatalogDocument } from "../hooks/useDocumentMention";
import "./ChatContainer.css";
import SimpleMessageInput from "./SimpleMessageInput";

interface ChatContainerProps {
	messages: UIMessage[];
	isLoading: boolean;
	isStopping?: boolean;
	input: string;
	onInputChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
	onSubmit: (e: React.FormEvent) => void;
	onKeyPress: (e: React.KeyboardEvent) => void;
	/** Re-ask the question behind a failed answer (by the answer's UI id). */
	onRegenerateMessage?: (assistantId: string) => void;
	streamingMessageId?: string | null;
	selectedVersions?: SelectedVersions;
	onSelectVersion?: (questionMessageId: number, versionMessageId: number) => void;
	variant?: "default" | "side-panel";
	containerRef: RefObject<HTMLDivElement | null>;
	bottomRef: RefObject<HTMLDivElement | null>;
	hasMoreMessages?: boolean;
	isLoadingMore?: boolean;
	isLoadingAll?: boolean;
	onLoadMore?: () => void;
	chatId?: string | null;
	/** The chat's arm, threaded to AssistantMessage for narration routing. */
	chatType?: string | null;
	onStopStreaming?: () => void;
	selectedDocs?: CatalogDocument[];
	onDocRemove?: (docId: string) => void;
	mentionDropdown?: React.ReactNode;
	onOpenTrace?: (msgId: string) => void;
	onOpenEvidence?: (msgId: string) => void;
	/** Open an answer's context graph in the host, instead of navigating (DOP-492). */
	onOpenGraph?: (msgId: string) => void;
	/** The answer whose view the host has open, so its pill can show it. */
	openView?: { kind: "audit" | "graph"; msgId: string } | null;
	inspectionCounts?: Record<string, number>;
	onInspectColumn?: (msgId: string, column: LoanTapePreviewColumn) => void;
	/**
	 * What an EMPTY thread shows in place of the hero (DOP-441). A host such
	 * as the Analyst rail wants the thread's own shape from the first turn —
	 * composer docked at the bottom, a line of guidance above it — rather
	 * than a landing-page greeting in a 440px column. When set, the composer
	 * renders even with no messages.
	 */
	emptyState?: React.ReactNode;
	placeholder?: string;
}

const ChatContainer: React.FC<ChatContainerProps> = ({
	messages,
	isLoading,
	isStopping,
	input,
	onInputChange,
	onSubmit,
	onKeyPress,
	onRegenerateMessage,
	streamingMessageId,
	selectedVersions,
	onSelectVersion,
	variant,
	containerRef,
	bottomRef,
	hasMoreMessages,
	isLoadingMore,
	isLoadingAll,
	onLoadMore,
	chatId,
	chatType,
	onStopStreaming,
	selectedDocs,
	onDocRemove,
	mentionDropdown,
	onOpenTrace,
	onOpenEvidence,
	onOpenGraph,
	openView,
	inspectionCounts,
	onInspectColumn,
	emptyState,
	placeholder = "Ask anything",
}) => {
	const sentinelRef = useRef<HTMLDivElement>(null);
	const onLoadMoreRef = useRef(onLoadMore);
	onLoadMoreRef.current = onLoadMore;

	const isLoadingMoreRef = useRef(isLoadingMore);
	isLoadingMoreRef.current = isLoadingMore;

	const isLoadingAllRef = useRef(isLoadingAll);
	isLoadingAllRef.current = isLoadingAll;

	const handleIntersect = useCallback((entries: IntersectionObserverEntry[]) => {
		if (entries[0]?.isIntersecting && !isLoadingMoreRef.current && !isLoadingAllRef.current) {
			onLoadMoreRef.current?.();
		}
	}, []);

	useEffect(() => {
		const el = sentinelRef.current;
		if (!el || !hasMoreMessages) return;
		const observer = new IntersectionObserver(handleIntersect, {
			root: containerRef.current,
			threshold: 0,
			rootMargin: "200px 0px 0px 0px",
		});
		observer.observe(el);
		return () => observer.disconnect();
	}, [hasMoreMessages, handleIntersect, containerRef]);

	return (
		<div
			className={`chat-container-wrapper ${
				variant === "side-panel" ? "chat-side-panel" : ""
			}`}
		>
			<div ref={containerRef} className="chat-messages-area">
				{messages.length === 0 && emptyState !== undefined && (
					<div className="m-auto max-w-xs px-4 text-center text-sm text-text-muted">
						{emptyState}
					</div>
				)}
				<div className="chat-messages-content">
					{hasMoreMessages && (
						<div ref={sentinelRef} className="chat-load-more">
							<span className="chat-load-more-text">
								{isLoadingMore ? "Loading older messages…" : "Scroll up for more"}
							</span>
						</div>
					)}
					<MessageList
						messages={messages}
						isLoading={isLoading}
						onRegenerateMessage={onRegenerateMessage}
						streamingMessageId={streamingMessageId}
						selectedVersions={selectedVersions}
						onSelectVersion={onSelectVersion}
						bottomRef={bottomRef}
						chatId={chatId ?? null}
						chatType={chatType}
						onOpenTrace={onOpenTrace}
						onOpenEvidence={onOpenEvidence}
						onOpenGraph={onOpenGraph}
						openView={openView}
						inspectionCounts={inspectionCounts}
						onInspectColumn={onInspectColumn}
					/>
				</div>
			</div>

			{/* Input at bottom when messages exist - Fixed positioning */}
			{(messages.length > 0 || emptyState !== undefined) && (
				<div className="chat-input-area">
					<SimpleMessageInput
						input={input}
						isLoading={isLoading}
						isStopping={isStopping}
						onInputChange={onInputChange}
						onSubmit={onSubmit}
						onKeyPress={onKeyPress}
						onStopStreaming={onStopStreaming}
						placeholder={placeholder}
						selectedDocs={selectedDocs}
						onDocRemove={onDocRemove}
						mentionDropdown={mentionDropdown}
					/>
				</div>
			)}
		</div>
	);
};

export default ChatContainer;
